import { createTheme } from "@mui/material/styles";

export const designTokens = {
  primary: "#292929",
  primaryHover: "#171717",
  primaryDark: "#292929",
  primarySoft: "#F5F5F5",
  purple: "#292929",
  cyan: "#3B82F6",
  green: "#16A34A",
  orange: "#F59E0B",
  red: "#F87171",
  ink: "#292929",
  text: "#5D5D5D",
  muted: "#9E9E9E",
  border: "#F5F5F5",
  page: "#FFFFFF",
  pageMuted: "#FAFAFA",
};

const sfPro = [
  '"SF Pro Text"',
  '"SF Pro Display"',
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "sans-serif",
].join(",");

const textStyle = {
  fontFamily: sfPro,
  letterSpacing: "-0.15px",
};

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: designTokens.primary, light: "#5D5D5D", dark: designTokens.primaryDark, contrastText: "#FFFFFF" },
    secondary: { main: designTokens.purple },
    success: { main: designTokens.green },
    warning: { main: designTokens.orange },
    error: { main: designTokens.red },
    info: { main: designTokens.cyan },
    background: { default: designTokens.pageMuted, paper: designTokens.page },
    text: { primary: designTokens.ink, secondary: designTokens.text, disabled: designTokens.muted },
    divider: designTokens.border,
  },
  typography: {
    fontFamily: sfPro,
    allVariants: { ...textStyle, fontSize: "14px", lineHeight: "20px", fontWeight: 400 },
    h1: { ...textStyle, fontSize: "24px", lineHeight: "32px", fontWeight: 600 },
    h2: { ...textStyle, fontSize: "16px", lineHeight: "20px", fontWeight: 600 },
    h3: { ...textStyle, fontSize: "16px", lineHeight: "20px", fontWeight: 600 },
    h4: { ...textStyle, fontSize: "16px", lineHeight: "20px", fontWeight: 600 },
    h5: { ...textStyle, fontSize: "16px", lineHeight: "20px", fontWeight: 600 },
    h6: { ...textStyle, fontSize: "16px", lineHeight: "20px", fontWeight: 600 },
    body1: { ...textStyle, fontSize: "14px", lineHeight: "20px", fontWeight: 400 },
    body2: { ...textStyle, fontSize: "14px", lineHeight: "20px", fontWeight: 400 },
    subtitle1: { ...textStyle, fontSize: "13px", lineHeight: "16px", fontWeight: 500 },
    subtitle2: { ...textStyle, fontSize: "13px", lineHeight: "16px", fontWeight: 500 },
    button: { ...textStyle, fontSize: "13px", lineHeight: "16px", fontWeight: 500, textTransform: "none" },
    overline: { ...textStyle, fontSize: "12px", lineHeight: "16px", fontWeight: 500, letterSpacing: "-0.15px", textTransform: "none" },
    caption: { ...textStyle, fontSize: "12px", lineHeight: "16px", fontWeight: 400 },
  },
  shape: { borderRadius: 16 },
  spacing: 8,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          minWidth: 320,
          minHeight: "100dvh",
          overflowX: "hidden",
          backgroundColor: designTokens.pageMuted,
          color: designTokens.ink,
          fontFamily: sfPro,
          letterSpacing: "-0.15px",
          textRendering: "optimizeLegibility",
          WebkitFontSmoothing: "antialiased",
        },
        "::selection": { backgroundColor: "rgba(41,41,41,.14)" },
        "@media (prefers-reduced-motion: reduce)": {
          "*, *::before, *::after": { animationDuration: "0.01ms !important", transitionDuration: "0.01ms !important" },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: 36,
          borderRadius: 999,
          padding: "0 12px",
          transition: "background-color 150ms ease, box-shadow 150ms ease",
          "&:focus-visible": { outline: "2px solid #292929", outlineOffset: 2 },
        },
        sizeSmall: { minHeight: 36, padding: "0 12px" },
        sizeLarge: { minHeight: 36, padding: "0 12px" },
        containedPrimary: {
          backgroundColor: designTokens.primary,
          "&:hover": { backgroundColor: designTokens.primaryHover },
        },
        outlined: {
          borderColor: designTokens.border,
          backgroundColor: "#FFFFFF",
          "&:hover": { borderColor: "#D4D4D4", backgroundColor: "#FAFAFA" },
        },
      },
    },
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" }, outlined: { borderColor: designTokens.border } } },
    MuiCard: { styleOverrides: { root: { borderRadius: 16, border: `1px solid ${designTokens.border}`, boxShadow: "none", backgroundColor: "#FFFFFF" } } },
    MuiCardContent: { styleOverrides: { root: { padding: 16, "&:last-child": { paddingBottom: 16 } } } },
    MuiTextField: { defaultProps: { variant: "outlined" } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { minHeight: 36, borderRadius: 8, backgroundColor: "#FFFFFF", ...textStyle, "&.Mui-focused": { boxShadow: "0 0 0 2px rgba(41,41,41,.12)" } },
        input: { ...textStyle, fontSize: "14px", lineHeight: "20px" },
        notchedOutline: { borderColor: "#D4D4D4" },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { height: 20, borderRadius: 999, fontWeight: 500, ...textStyle },
        sizeSmall: { height: 20 },
        label: { paddingLeft: 6, paddingRight: 6, fontSize: "12px", lineHeight: "16px" },
      },
    },
    MuiSvgIcon: { defaultProps: { fontSize: "small" } },
    MuiTooltip: { styleOverrides: { tooltip: { borderRadius: 8, backgroundColor: designTokens.ink, fontSize: "12px", lineHeight: "16px", padding: "8px 10px" } } },
    MuiDivider: { styleOverrides: { root: { borderColor: designTokens.border } } },
  },
});

export default theme;
